import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Link,
  useNavigate,
  useParams,
} from 'react-router-dom';

import UpdateNoticeLayout
  from '../components/UpdateNoticeLayout';

import {
  getUpdateNotice,
  getUpdateNotices,
  saveUpdateNotice,
} from '../services/updateNoticeService';

import {
  formatNoticeDate,
} from '../config/updateNoticeConfig';

import {
  TOP_LEVEL_NUMBERED_LINE_REGEX,
  buildAutoTitle,
  extractOverviewFromDetails,
  extractTopLevelNumberedLines,
  getChildContentLevel,
  getHierarchicalNumberingLevel,
  getImageMarkerCaption,
  getImageMarkerIndex,
  getLevelTextStyle,
  parseDetailContentLines,
  parseDetailLines,
  parseUpdateNoticeDetailItems,
  replaceHashHeadingMarker,
  resolveMarkerImage,
} from '../data/updateNoticeDetailContent';

import useUpdateNoticeLanguage
  from '../hooks/useUpdateNoticeLanguage';

import './UpdateNoticePage.css';

const PROGRAM_OPTIONS = [
  'Hi-CPS',
  'Hi-Link',
  'PG-Solution',
];

const PLATFORM_OPTIONS = {
  'Hi-CPS': [
    'Web',
    'Android',
    'iOS',
    'NC Agent',
  ],

  'Hi-Link': [],

  'PG-Solution': [],
};

const AUTO_TRANSLATE_TARGET_LANGUAGES = [
  'tr',
  'pt',
  'hi',
  'es',
  'de',
  'zh',
  'it',
  'pl',
  'cs',
  'uk',
  'uz',
];

const TRANSLATED_SUFFIX_REGEX =
  /_(En|Tr|Pt|Hi|Es|De|Zh|It|Pl|Cs|Uk|Uz|Global)$/i;

const EDITOR_GUIDE = `# 업데이트 공지 작성 및 에디터 사용 가이드

이 문서는 Hi-Portal 시스템에서 업데이트 공지를 생성/수정하는 전체 절차, 에디터 사용법, 저장 구조, 한글/글로벌 공지 관리 방법을 안내합니다.

---

## 1. 업데이트 공지 생성 방법

1. **공지 생성 진입**
   - 상세 페이지 **오른쪽 상단의 "수정, 삭제" 버튼**을 클릭하면 공지 작성 에디터로 이동하여 해당 공지를 수정할 수 있습니다.
   - 메인화면(공지 목록) **왼쪽 하단의 "공지 생성" 버튼**을 클릭하면 새 공지 작성 에디터로 이동합니다.

2. **공지 정보 입력**
   - 프로그램, 플랫폼, 버전, 날짜 등 메타 정보를 입력합니다.
   - 상세 내용은 에디터(텍스트박스)에 직접 입력합니다.
     - # 제목 또는 1. 항목 등으로 구분하여 작성하면 자동으로 목차와 계층 스타일이 적용됩니다.
    - 이미지 첨부는 "이미지 파일 첨부" 버튼을 눌러 업로드하면 [image:n|] 마커가 자동 삽입됩니다.
    - 이미지 주석은 [image:n|주석] 형식으로 입력하면 이미지 아래에 표시됩니다.
     - Tab 키로 들여쓰기, Shift+Tab으로 내어쓰기가 가능합니다.

3. **미리보기 확인**
   - 우측 미리보기 영역에서 입력한 내용이 실시간으로 렌더링됩니다.
   - 목차(What's New)와 이미지, 계층 스타일 등 실제 공지와 거의 동일하게 확인할 수 있습니다.

4. **공지 저장**
   - 모든 입력이 끝나면 **하단의 "저장" 버튼**을 클릭합니다.
   - 저장이 완료되면 data/updatenotice 경로에서 json파일(index.json, title.json)을 s3에 저장합니다.
  
 ※ 저장된 index.json의 내용에 누락되거나 잘못된 부분이 없는지 확인하고, 이상이 있으면 수정해야 합니다.

---

## 2. index.json 저장 구조 및 다국어 관리

- data/updatenotice/index.json 파일에는 **한글/영문 공지 메타 정보가 함께 저장**됩니다.
- 각 공지 항목은 아래와 같이 한글/영문 필드를 모두 포함합니다.

   json
{
  "program": "PG-Solution",
  "version": "v1.2.3",
  "platform": "",
  "date": "2026-04-10",
  "title": "공지 제목",
  "overview": "공지 요약",
}

- 화면에서는 언어 설정에 따라 title(한글) 또는 title_En(영문) 필드를 선택적으로 보여줍니다.

---

## 3. 상세 공지 파일 구조

- 각 공지의 상세 내용은 아래와 같이 한글/글로벌 파일이 별도로 존재합니다.
- 한글: data/updatenotice/[title].json
- 글로벌: data/updatenotice/[title_Global].json

 json
// 한글 예시
{
  "title": "공지 제목",
  "details": "# 주요 업데이트\n- 내용 ...\n[image:1]",
  "image": [
    "https://.../image1.png",
    "https://.../image2.png"
  ]
}

// 글로벌 예시
{
  "title": "공지 제목_Global",
  "details": "# Main Update\n- Content ...\n[image:1]",
  "image": [
    "https://.../image1.png",
    "https://.../image2.png"
  ]
}
  
---

## 4. 에디터 입력 및 미리보기 규칙

- # 제목 : 헤더(제목)로 인식 (띄어쓰기 필수)
- 1., 1.1., 2. : 번호 블록(계층)으로 인식
- [image:n] : 이미지 위치 지정 (기존 방식)
- [image:n|주석] : 이미지 위치 + 이미지 주석 표시
- Tab/Shift+Tab : 들여쓰기/내어쓰기
- 표, 코드블록, 링크 등은 지원하지 않음
- 입력한 상세내용이 우측 미리보기에서 실시간 렌더링
- What's New(목차) 자동 생성 및 클릭 시 해당 블록으로 이동
- 각 블록은 계층별 스타일(1~4레벨)로 구분
- [image:n] 위치에 업로드한 이미지 표시
- 미리보기와 실제 공지 화면이 100% 일치하지 않을 수 있음
- #제목처럼 해시 뒤에 띄어쓰기가 없으면 헤더로 인식되지 않음
- 이미지 마커와 실제 업로드 이미지 개수가 다르면 [missing image n]으로 표시

---`

function getToday() {
  return new Date()
    .toISOString()
    .slice(0, 10);
}

function getPlatformOptions(
  program,
) {
  return Array.isArray(
    PLATFORM_OPTIONS[program],
  )
    ? PLATFORM_OPTIONS[program]
    : [];
}

function createEmptyForm(
  latestVersion = '',
) {
  const program =
    PROGRAM_OPTIONS[0];

  const platformOptions =
    getPlatformOptions(
      program,
    );

  return {
    seq: '',
    program,
    platform:
      platformOptions[0] ||
      '',
    version:
      latestVersion ||
      '',
    date:
      getToday(),
    details: '',
    images: [],
  };
}

function fileToDataUrl(
  file,
) {
  return new Promise(
    (
      resolve,
      reject,
    ) => {
      const reader =
        new FileReader();

      reader.onload =
        () => {
          resolve(
            String(
              reader.result ||
                '',
            ),
          );
        };

      reader.onerror =
        () => {
          reject(
            new Error(
              '이미지 파일을 읽지 못했습니다.',
            ),
          );
        };

      reader.readAsDataURL(
        file,
      );
    },
  );
}

function normalizeVersion(
  value,
) {
  return String(
    value || '',
  )
    .trim()
    .replace(
      TRANSLATED_SUFFIX_REGEX,
      '',
    );
}

function publishUpdateNoticeChanged(
  seq,
) {
  const channelName =
    'hi-portal-updatenotices';

  const payload = {
    action: 'save',
    seq: seq || null,
    timestamp:
      Date.now(),
  };

  try {
    if (
      'BroadcastChannel' in
      window
    ) {
      const channel =
        new BroadcastChannel(
          channelName,
        );

      channel.postMessage(
        payload,
      );

      channel.close();

      return;
    }

    window.localStorage.setItem(
      channelName,
      JSON.stringify(
        payload,
      ),
    );
  } catch (error) {
    console.error(
      '[UpdateNoticeEditor] 변경 알림 실패:',
      error,
    );
  }
}

export default function UpdateNoticeEditorPage() {
  const {
    seq,
  } = useParams();

  const navigate =
    useNavigate();

  const detailsRef =
    useRef(null);

  const imagePreviewsRef =
    useRef([]);

  const {
    noticeLanguage,
  } = useUpdateNoticeLanguage();

  const isEditMode =
    Boolean(seq);

  const [
    form,
    setForm,
  ] = useState(
    () =>
      createEmptyForm(),
  );

  const [
    localImagePreviews,
    setLocalImagePreviews,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState('');

  const [
    isGuideOpen,
    setIsGuideOpen,
  ] = useState(false);

  useEffect(() => {
    imagePreviewsRef.current =
      localImagePreviews;
  }, [
    localImagePreviews,
  ]);

  useEffect(() => {
    return () => {
      imagePreviewsRef.current.forEach(
        (
          preview,
        ) => {
          if (
            preview.objectUrl
          ) {
            URL.revokeObjectURL(
              preview.objectUrl,
            );
          }
        },
      );
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const initialize =
      async () => {
        setLoading(true);
        setError('');

        try {
          const [
            editingNotice,
            notices,
          ] =
            await Promise.all([
              isEditMode
                ? getUpdateNotice(
                    seq,
                    noticeLanguage,
                  )
                : Promise.resolve(
                    null,
                  ),

              getUpdateNotices(
                noticeLanguage,
              ),
            ]);

          if (!mounted) {
            return;
          }

          if (
            isEditMode &&
            !editingNotice
          ) {
            throw new Error(
              '해당 업데이트 공지를 찾을 수 없습니다.',
            );
          }

          imagePreviewsRef.current.forEach(
            (
              preview,
            ) => {
              if (
                preview.objectUrl
              ) {
                URL.revokeObjectURL(
                  preview.objectUrl,
                );
              }
            },
          );

          setLocalImagePreviews(
            [],
          );

          const program =
            editingNotice
              ?.program ||
            PROGRAM_OPTIONS[0];

          const editingListNotice =
            isEditMode
              ? notices.find(
                  (notice) =>
                    Number(notice.seq) ===
                    Number(seq),
                )
              : null;

          const platformOptions =
            getPlatformOptions(
              program,
            );

          setForm({
            seq:
              editingNotice
                ?.seq ||
              '',

            program,

            platform:
              program === 'Hi-Link'
                ? ''
                : editingNotice?.platform ||
                  platformOptions[0] ||
                  '',

            version:
              normalizeVersion(
                editingNotice
                  ?.version ||
                editingNotice
                  ?.latestVersion ||
                editingListNotice
                  ?.version ||
                editingListNotice
                  ?.latestVersion ||
                '',
              ),

            date:
              String(
                editingNotice
                  ?.date ||
                getToday(),
              ).slice(
                0,
                10,
              ),

            details:
              editingNotice
                ?.details ||
              '',

            images:
              Array.isArray(
                editingNotice
                  ?.images,
              )
                ? editingNotice
                    .images
                : [],
          });
        } catch (
          initializeError
        ) {
          if (!mounted) {
            return;
          }

          console.error(
            '[UpdateNoticeEditorPage] 초기화 실패:',
            initializeError,
          );

          setError(
            initializeError
              ?.message ||
              '공지 편집 화면을 불러오지 못했습니다.',
          );
        } finally {
          if (mounted) {
            setLoading(false);
          }
        }
      };

    initialize();

    return () => {
      mounted = false;
    };
  }, [
    isEditMode,
    noticeLanguage,
    seq,
  ]);

  const onChange = (
    key,
    value,
  ) => {
    setForm(
      (
        previous,
      ) => ({
        ...previous,

        [key]:
          value,
      }),
    );
  };

  const onProgramChange = (
    program,
  ) => {
    const platformOptions =
      getPlatformOptions(
        program,
      );

    setForm(
      (
        previous,
      ) => ({
        ...previous,

        program,

        platform:
          platformOptions[0] ||
          '',
      }),
    );
  };

  const onDetailKeyDown = (
    event,
  ) => {
    if (
      event.key !== 'Tab'
    ) {
      return;
    }

    event.preventDefault();

    const textarea =
      event.currentTarget;

    const start =
      textarea.selectionStart ||
      0;

    const end =
      textarea.selectionEnd ||
      start;

    const value =
      textarea.value ||
      '';

    if (start === end) {
      const next =
        `${value.slice(0, start)}\t${value.slice(end)}`;

      onChange(
        'details',
        next,
      );

      window.requestAnimationFrame(
        () => {
          textarea.focus();

          textarea.setSelectionRange(
            start + 1,
            start + 1,
          );
        },
      );

      return;
    }

    const blockStart =
      value.lastIndexOf(
        '\n',
        start - 1,
      ) + 1;

    const blockEndCandidate =
      value.indexOf(
        '\n',
        end,
      );

    const blockEnd =
      blockEndCandidate ===
      -1
        ? value.length
        : blockEndCandidate;

    const block =
      value.slice(
        blockStart,
        blockEnd,
      );

    const lines =
      block.split(
        '\n',
      );

    const indented =
      lines
        .map(
          (
            line,
          ) =>
            `\t${line}`,
        )
        .join(
          '\n',
        );

    const next =
      `${value.slice(0, blockStart)}${indented}${value.slice(blockEnd)}`;

    onChange(
      'details',
      next,
    );

    window.requestAnimationFrame(
      () => {
        textarea.focus();

        textarea.setSelectionRange(
          start + 1,
          end +
            lines.length,
        );
      },
    );
  };

  const insertAtCursor = (
    textToInsert,
  ) => {
    const textarea =
      detailsRef.current;

    const current =
      form.details ||
      '';

    if (!textarea) {
      onChange(
        'details',
        `${current}${textToInsert}`,
      );

      return;
    }

    const start =
      textarea.selectionStart ||
      0;

    const end =
      textarea.selectionEnd ||
      start;

    const next =
      `${current.slice(0, start)}${textToInsert}${current.slice(end)}`;

    const nextPosition =
      start +
      textToInsert.length;

    onChange(
      'details',
      next,
    );

    window.requestAnimationFrame(
      () => {
        textarea.focus();

        textarea.setSelectionRange(
          nextPosition,
          nextPosition,
        );
      },
    );
  };

  const onUploadImage = (
    event,
  ) => {
    const files =
      Array.from(
        event.target.files ||
        [],
      );

    if (
      files.length ===
      0
    ) {
      return;
    }

    const startIndex =
      form.images.length +
      localImagePreviews.length +
      1;

    const previews =
      files.map(
        (
          file,
          index,
        ) => ({
          id:
            `${Date.now()}-${index}-${file.name}`,

          file,

          marker:
            `[image:${startIndex + index}|]`,

          objectUrl:
            URL.createObjectURL(
              file,
            ),
        }),
      );

    setLocalImagePreviews(
      (
        previous,
      ) => [
        ...previous,
        ...previews,
      ],
    );

    const markerBlock =
      previews
        .map(
          (
            preview,
          ) =>
            preview.marker,
        )
        .join(
          '\n',
        );

    const prefix =
      form.details &&
      !form.details.endsWith(
        '\n',
      )
        ? '\n'
        : '';

    insertAtCursor(
      `${prefix}${markerBlock}\n`,
    );

    event.target.value =
      '';
  };

  const removeLocalImage = (
    targetId,
  ) => {
    setLocalImagePreviews(
      (
        previous,
      ) => {
        const target =
          previous.find(
            (
              preview,
            ) =>
              preview.id ===
              targetId,
          );

        if (
          target?.objectUrl
        ) {
          URL.revokeObjectURL(
            target.objectUrl,
          );
        }

        return previous.filter(
          (
            preview,
          ) =>
            preview.id !==
            targetId,
        );
      },
    );
  };

  const previewImages =
    useMemo(
      () => {
        const existing =
          form.images.map(
            (
              url,
              index,
            ) => ({
              id:
                `existing-${index}`,

              url,

              type:
                'existing',
            }),
          );

        const uploaded =
          localImagePreviews.map(
            (
              preview,
            ) => ({
              id:
                preview.id,

              url:
                preview.objectUrl,

              type:
                'uploaded',
            }),
          );

        return [
          ...existing,
          ...uploaded,
        ];
      },
      [
        form.images,
        localImagePreviews,
      ],
    );

  const previewImageSources =
    useMemo(
      () =>
        previewImages.map(
          (
            image,
          ) =>
            image.url,
        ),
      [previewImages],
    );

  const previewTitle =
    useMemo(
      () =>
        buildAutoTitle(
          form.program,
          form.platform,
          form.version,
        ) ||
        '-',
      [
        form.platform,
        form.program,
        form.version,
      ],
    );

  const sourceDetails =
    useMemo(
      () =>
        String(
          form.details ||
          '',
        ),
      [form.details],
    );

  const detailItems =
    useMemo(
      () =>
        parseUpdateNoticeDetailItems(
          sourceDetails,
        ),
      [sourceDetails],
    );

  const previewTopLevelLinks =
    useMemo(
      () =>
        detailItems.flatMap(
          (
            item,
          ) => {
            if (
              item.isHashHeading
            ) {
              return extractTopLevelNumberedLines(
                item.content,
              ).map(
                (
                  line,
                  index,
                ) => ({
                  key:
                    `${item.id}-sub-${index}`,

                  targetId:
                    `preview-${item.id}-subline-${index + 1}`,

                  label:
                    line,

                  isNested:
                    true,
                }),
              );
            }

            return [
              {
                key:
                  item.id,

                targetId:
                  `preview-${item.id}`,

                label:
                  `${item.order}. ${item.title}`,

                isNested:
                  false,
              },
            ];
          },
        ),
      [detailItems],
    );

  const platformOptions =
    getPlatformOptions(
      form.program,
    );

  const hasDetailsContent =
    sourceDetails
      .trim()
      .length >
      0 ||
    detailItems.length >
      0;

  const hasTopLevelLinks =
    previewTopLevelLinks
      .length >
    0;

  const onPreviewLinkClick = (
    event,
    targetId,
  ) => {
    event.preventDefault();

    const target =
      document.getElementById(
        targetId,
      );

    target?.scrollIntoView({
      behavior:
        'smooth',

      block:
        'start',
    });
  };

  const renderPreviewImageBlock = ({
    line,
    key,
    contentLevel,
    forceNormalWeight = false,
  }) => {
    const markerIndex =
      getImageMarkerIndex(
        line,
      );

    if (
      markerIndex ===
      null
    ) {
      return null;
    }

    const imageSource =
      resolveMarkerImage(
        line,
        previewImageSources,
      );

    const caption =
      getImageMarkerCaption(
        line,
      );

    if (!imageSource) {
      return (
        <p
          key={key}
          className="updatenotice-missing-image"
          style={getLevelTextStyle(
            contentLevel,
            forceNormalWeight,
          )}
        >
          {`[missing image ${markerIndex + 1}]`}
        </p>
      );
    }

    return (
      <figure
        key={key}
        className="updatenotice-editor-preview-image-figure"
      >
        <img
          src={imageSource}
          onLoad={(event) => {
            const image = event.currentTarget;
            image.classList.toggle(
              'is-tall-2x',
              image.naturalHeight >= image.naturalWidth * 2,
            );
            image.classList.toggle(
              'is-tall-3x',
              image.naturalHeight >= image.naturalWidth * 3,
            );
            image.classList.toggle(
              'is-tall-4x',
              image.naturalHeight >= image.naturalWidth * 4,
            );
          }}
          alt={`preview-${markerIndex + 1}`}
          className="updatenotice-editor-preview-image"
        />

        {caption && (
          <figcaption className="updatenotice-editor-preview-caption">
            {caption}
          </figcaption>
        )}
      </figure>
    );
  };

  const renderPlainContentWithImages = (
    content,
  ) => {
    const lines =
      parseDetailLines(
        content,
      );

    let currentHeadingLevel =
      1;

    return (
      <div className="updatenotice-render-blocks">
        {lines.map(
          (
            line,
            index,
          ) => {
            const trimmed =
              String(
                line ||
                '',
              ).trim();

            const isHashHeading =
              /^#{1,6}(?:\s*.*)?$/.test(
                trimmed,
              );

            const numberingLevel =
              getHierarchicalNumberingLevel(
                line,
              );

            if (isHashHeading) {
              currentHeadingLevel =
                1;
            }

            if (
              numberingLevel !==
              null
            ) {
              currentHeadingLevel =
                numberingLevel;
            }

            const contentLevel =
              numberingLevel ||
              getChildContentLevel(
                currentHeadingLevel,
              );

            const imageElement =
              renderPreviewImageBlock({
                line,

                key:
                  `plain-image-${index}`,

                contentLevel,
              });

            if (imageElement) {
              return imageElement;
            }

            if (!trimmed) {
              return (
                <div
                  key={`plain-blank-${index}`}
                  className="updatenotice-detail-blank"
                />
              );
            }

            return (
              <p
                key={`plain-line-${index}`}
                className="updatenotice-detail-line"
                style={getLevelTextStyle(
                  contentLevel,
                )}
              >
                {replaceHashHeadingMarker(
                  line,
                )}
              </p>
            );
          },
        )}
      </div>
    );
  };

  const renderDetailContent = (
    content,
    anchorPrefix,
    baseLevelOffset = 0,
    forceNormalWeight = false,
  ) => {
    const blocks =
      parseDetailContentLines(
        content,
      );

    if (
      blocks.length ===
      0
    ) {
      return null;
    }

    let numberedLineIndex =
      0;

    let currentHeadingLevel =
      1;

    return (
      <div className="updatenotice-render-blocks">
        {blocks.map(
          (
            block,
            index,
          ) => {
            const numberingLevel =
              getHierarchicalNumberingLevel(
                block.text,
              );

            const normalWeight =
              forceNormalWeight &&
              numberingLevel ===
                null;

            if (
              block.type ===
              'subheading'
            ) {
              currentHeadingLevel =
                Math.min(
                  (
                    numberingLevel ||
                    block.level
                  ) +
                    baseLevelOffset,
                  4,
                );
            } else if (
              numberingLevel !==
              null
            ) {
              currentHeadingLevel =
                Math.min(
                  numberingLevel +
                    baseLevelOffset,
                  4,
                );
            }

            const contentLevel =
              block.type ===
              'subheading'
                ? currentHeadingLevel
                : numberingLevel !==
                    null
                  ? Math.min(
                      numberingLevel +
                        baseLevelOffset,
                      4,
                    )
                  : getChildContentLevel(
                      currentHeadingLevel,
                    );

            if (
              block.type ===
              'blank'
            ) {
              return (
                <div
                  key={`blank-${index}`}
                  className="updatenotice-detail-blank"
                />
              );
            }

            if (
              block.type ===
              'subheading'
            ) {
              return (
                <p
                  key={`subheading-${index}`}
                  className="updatenotice-detail-subheading"
                  style={getLevelTextStyle(
                    contentLevel,
                    normalWeight,
                  )}
                >
                  {replaceHashHeadingMarker(
                    block.text,
                  )}
                </p>
              );
            }

            const imageElement =
              renderPreviewImageBlock({
                line:
                  block.text,

                key:
                  `detail-image-${index}`,

                contentLevel,

                forceNormalWeight:
                  normalWeight,
              });

            if (imageElement) {
              return imageElement;
            }

            const shouldCreateAnchor =
              Boolean(
                anchorPrefix,
              ) &&
              block.level === 1 &&
              TOP_LEVEL_NUMBERED_LINE_REGEX.test(
                String(
                  block.text ||
                    '',
                ).trim(),
              );

            if (
              shouldCreateAnchor
            ) {
              numberedLineIndex +=
                1;
            }

            return (
              <p
                key={`detail-line-${index}`}
                id={
                  shouldCreateAnchor
                    ? `${anchorPrefix}-${numberedLineIndex}`
                    : undefined
                }
                className="updatenotice-detail-line"
                style={getLevelTextStyle(
                  contentLevel,
                  normalWeight,
                )}
              >
                {replaceHashHeadingMarker(
                  block.text,
                )}
              </p>
            );
          },
        )}
      </div>
    );
  };

  const onSubmit =
    async (
      event,
    ) => {
      event.preventDefault();

      if (
        !form.program
      ) {
        window.alert(
          '프로그램을 선택해 주세요.',
        );

        return;
      }

      if (
        !form.version.trim()
      ) {
        window.alert(
          '버전을 입력해 주세요.',
        );

        return;
      }

      if (
        !form.date
      ) {
        window.alert(
          '적용일을 선택해 주세요.',
        );

        return;
      }

      if (
        !form.details.trim()
      ) {
        window.alert(
          '공지 상세 내용을 입력해 주세요.',
        );

        return;
      }

      setSaving(true);

      try {
        const existingImages =
          form.images.filter(
            (
              image,
            ) =>
              typeof image ===
              'string',
          );

        const uploadedImages =
          await Promise.all(
            localImagePreviews.map(
              (
                preview,
              ) =>
                fileToDataUrl(
                  preview.file,
                ),
            ),
          );

        const allImages = [
          ...existingImages,
          ...uploadedImages,
        ];

        const version =
          normalizeVersion(
            form.version,
          );

        const title =
          buildAutoTitle(
            form.program,
            form.platform,
            version,
          ).replace(
            TRANSLATED_SUFFIX_REGEX,
            '',
          );

        let overview =
          extractOverviewFromDetails(
            form.details,
          );

        if (!overview) {
          overview =
            String(
              form.details ||
                '',
            )
              .split(
                '\n',
              )
              .find(
                (
                  line,
                ) =>
                  line.trim(),
              ) ||
            '';
        }

        const normalizedLanguage =
          String(
            noticeLanguage ||
              'ko',
          )
            .trim()
            .toLowerCase();

        const saved =
          await saveUpdateNotice({
            ...(isEditMode
              ? {
                  seq:
                    Number(
                      seq,
                    ),
                }
              : {}),

            program:
              form.program,

            platform:
              form.platform,

            version,

            date:
              form.date,

            title,

            overview,

            details:
              form.details,

            images:
              allImages,

            noticeLang:
              normalizedLanguage,

            autoTranslate:
              normalizedLanguage ===
              'en',

            targetLangs:
              normalizedLanguage ===
              'en'
                ? AUTO_TRANSLATE_TARGET_LANGUAGES
                : [],
          });

        const savedSeq =
          saved?.seq ||
          form.seq ||
          seq;

        publishUpdateNoticeChanged(
          savedSeq,
        );

        window.alert(
          normalizedLanguage ===
            'en'
            ? '저장이 완료되었습니다. 자동 번역도 함께 처리되었습니다.'
            : '저장이 완료되었습니다.',
        );

        if (savedSeq) {
          navigate(
            `/updatenotices/${savedSeq}`,
            {
              replace:
                true,
            },
          );

          return;
        }

        navigate(
          '/updatenotices/admin',
          {
            replace:
              true,
          },
        );
      } catch (
        saveError
      ) {
        console.error(
          '[UpdateNoticeEditorPage] 저장 실패:',
          saveError,
        );

        window.alert(
          saveError?.message ||
            '공지 저장에 실패했습니다.',
        );
      } finally {
        setSaving(false);
      }
    };

  return (
    <UpdateNoticeLayout>
      <section className="updatenotice-hicps-editor">
        <header className="updatenotice-hicps-editor-header">
          <div>
            <span>
              UPDATE NOTICE EDITOR
            </span>

            <h2>
              {isEditMode
                ? '업데이트 공지 수정'
                : '업데이트 공지 작성'}
            </h2>
          </div>

          <div className="updatenotice-hicps-editor-header-actions">
            <button
              type="button"
              className="updatenotice-editor-guide-button"
              onClick={() =>
                setIsGuideOpen(
                  true,
                )
              }
            >
              작성 가이드
            </button>

            <Link
              to="/updatenotices/admin"
              className="updatenotice-secondary-button"
            >
              목록으로
            </Link>
          </div>
        </header>

        {loading && (
          <div className="updatenotice-state-card">
            공지 편집 화면을 준비하는 중입니다.
          </div>
        )}

        {!loading &&
          error && (
            <div className="updatenotice-state-card error">
              <strong>
                공지를 불러오지 못했습니다.
              </strong>

              <p>
                {error}
              </p>
            </div>
          )}

        {!loading &&
          !error && (
            <div className="updatenotice-hicps-editor-workspace">
              <form
                className="updatenotice-hicps-editor-form"
                onSubmit={
                  onSubmit
                }
              >
                <label>
                  <span>
                    프로그램
                  </span>

                  <select
                    value={
                      form.program
                    }
                    onChange={(
                      event,
                    ) =>
                      onProgramChange(
                        event.target
                          .value,
                      )
                    }
                  >
                    {PROGRAM_OPTIONS.map(
                      (
                        option,
                      ) => (
                        <option
                          key={
                            option
                          }
                          value={
                            option
                          }
                        >
                          {option}
                        </option>
                      ),
                    )}
                  </select>
                </label>

                {form.program !== 'Hi-Link' && (
                  <label>
                    <span>
                      플랫폼
                    </span>

                    <select
                      value={form.platform}
                      onChange={(event) =>
                        onChange(
                          'platform',
                          event.target.value,
                        )
                      }
                      disabled={platformOptions.length === 0}
                    >
                      {form.program === 'PG-Solution' && (
                        <option value="">
                          -
                        </option>
                      )}

                      {platformOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                <label>
                  <span>
                    버전
                  </span>

                  <input
                    value={
                      form.version
                    }
                    onChange={(
                      event,
                    ) =>
                      onChange(
                        'version',
                        event.target
                          .value,
                      )
                    }
                    placeholder="예: YY.MM.DD-N"
                  />
                </label>

                <label>
                  <span>
                    적용일
                  </span>

                  <input
                    type="date"
                    value={
                      form.date
                    }
                    onChange={(
                      event,
                    ) =>
                      onChange(
                        'date',
                        event.target
                          .value,
                      )
                    }
                  />
                </label>

                <label className="updatenotice-hicps-details-field">
                  <span className="updatenotice-hicps-details-row">
                    <span>
                      상세 내용
                    </span>

                    <span className="updatenotice-hicps-upload-button">
                      이미지 추가

                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={
                          onUploadImage
                        }
                      />
                    </span>
                  </span>

                  <textarea
                    ref={
                      detailsRef
                    }
                    rows={22}
                    value={
                      form.details
                    }
                    onChange={(
                      event,
                    ) =>
                      onChange(
                        'details',
                        event.target
                          .value,
                      )
                    }
                    onKeyDown={
                      onDetailKeyDown
                    }
                    placeholder={
                      '# 기능 개선\n1. 로그인 화면 개선\n1.1. 증상 및 개선\n- 증상: 기존 화면의 가독성이 낮음\n- 개선: 화면 구성을 개선\n\n[image:1|개선된 로그인 화면]'
                    }
                  />

                  <small className="updatenotice-hicps-details-guide">
                    # 제목, 번호 제목, Tab 들여쓰기, [image:1|캡션] 문법을 사용할 수 있습니다.
                  </small>
                </label>

                {localImagePreviews.length >
                  0 && (
                  <section className="updatenotice-hicps-uploaded-images">
                    <strong>
                      추가된 이미지
                    </strong>

                    <div>
                      {localImagePreviews.map(
                        (
                          preview,
                          index,
                        ) => (
                          <article
                            key={
                              preview.id
                            }
                          >
                            <img
                              src={
                                preview.objectUrl
                              }
                              alt={`추가 이미지 ${index + 1}`}
                            />

                            <span>
                              이미지{' '}
                              {form.images.length +
                                index +
                                1}
                            </span>

                            <button
                              type="button"
                              onClick={() =>
                                removeLocalImage(
                                  preview.id,
                                )
                              }
                            >
                              제거
                            </button>
                          </article>
                        ),
                      )}
                    </div>
                  </section>
                )}

                {noticeLanguage ===
                  'en' && (
                  <div className="updatenotice-hicps-auto-translate-notice">
                    영어 공지 저장 시 지원 언어 자동 번역이 함께 실행됩니다.
                  </div>
                )}

                <div className="updatenotice-hicps-editor-form-actions">
                  <Link
                    to="/updatenotices/admin"
                    className="updatenotice-secondary-button"
                  >
                    취소
                  </Link>

                  <button
                    type="submit"
                    className="updatenotice-primary-button"
                    disabled={
                      saving
                    }
                  >
                    {saving
                      ? '저장 중...'
                      : '공지 저장'}
                  </button>
                </div>
              </form>

              <section className="updatenotice-hicps-editor-preview">
                <div className="updatenotice-hicps-preview-label">
                  실시간 미리보기
                </div>

                <div className="updatenotice-hicps-preview-meta">
                  <span>
                    <strong>
                      프로그램:
                    </strong>{' '}
                    {form.program ||
                      '-'}
                  </span>

                  <span>
                    <strong>
                      플랫폼:
                    </strong>{' '}
                    {form.platform ||
                      '-'}
                  </span>

                  <span>
                    <strong>
                      버전:
                    </strong>{' '}
                    {form.version ||
                      '-'}
                  </span>

                  <span>
                    <strong>
                      날짜:
                    </strong>{' '}
                    {formatNoticeDate(
                      form.date,
                    )}
                  </span>
                </div>

                <h2 className="updatenotice-hicps-preview-title">
                  {previewTitle}
                </h2>

                <div className="updatenotice-hicps-preview-content">
                  {hasDetailsContent ? (
                    <>
                      {hasTopLevelLinks && (
                        <>
                          <h3 className="updatenotice-whats-new-title">
                            ◎ 주요 변경사항
                          </h3>

                          <nav className="updatenotice-detail-links">
                            {previewTopLevelLinks.map(
                              (
                                entry,
                              ) => (
                                <a
                                  key={
                                    entry.key
                                  }
                                  href={`#${entry.targetId}`}
                                  className={
                                    entry.isNested
                                      ? 'is-nested'
                                      : undefined
                                  }
                                  onClick={(
                                    event,
                                  ) =>
                                    onPreviewLinkClick(
                                      event,
                                      entry.targetId,
                                    )
                                  }
                                >
                                  {entry.label}
                                </a>
                              ),
                            )}
                          </nav>
                        </>
                      )}

                      {detailItems.length >
                      0 ? (
                        <div className="updatenotice-detail-items">
                          {detailItems.map(
                            (
                              item,
                            ) => (
                              <section
                                key={
                                  item.id
                                }
                                id={`preview-${item.id}`}
                                className="updatenotice-item-block"
                              >
                                <h3 className="updatenotice-item-title">
                                  {item.isHashHeading
                                    ? `◎${item.title ? ` ${item.title}` : ''}`
                                    : `${item.order}. ${item.title}`}
                                </h3>

                                {item.content &&
                                  renderDetailContent(
                                    item.content,

                                    `preview-${item.id}-subline`,

                                    0,

                                    item.isHashHeading,
                                  )}
                              </section>
                            ),
                          )}
                        </div>
                      ) : (
                        renderPlainContentWithImages(
                          sourceDetails,
                        )
                      )}
                    </>
                  ) : (
                    <div className="updatenotice-hicps-preview-empty">
                      상세 내용을 입력하면 이곳에 미리보기가 표시됩니다.
                    </div>
                  )}
                </div>
              </section>
            </div>
          )}
      </section>

      {isGuideOpen && (
        <div
          className="updatenotice-editor-guide-overlay"
          role="presentation"
          onMouseDown={() =>
            setIsGuideOpen(
              false,
            )
          }
        >
          <section
            className="updatenotice-editor-guide-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="updatenotice-editor-guide-title"
            onMouseDown={(
              event,
            ) =>
              event.stopPropagation()
            }
          >
            <header>
              <h2 id="updatenotice-editor-guide-title">
                업데이트 공지 작성 가이드
              </h2>

              <button
                type="button"
                onClick={() =>
                  setIsGuideOpen(
                    false,
                  )
                }
              >
                닫기
              </button>
            </header>

            <pre>
              {EDITOR_GUIDE}
            </pre>
          </section>
        </div>
      )}
    </UpdateNoticeLayout>
  );
}
